import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

// ─── Cloudinary Upload ────────────────────────────────────────────────────────
const uploadToCloudinary = async (file) => {
  const isVideo = file.type.startsWith("video/");
  const resourceType = isVideo ? "video" : "image";

  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", process.env.REACT_APP_UPLOAD_PRESET);

  const res = await axios.post(
    `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUD_NAME}/${resourceType}/upload`,
    formData,
    {
      onUploadProgress: (e) => {
        if (e.total) {
          const pct = Math.round((e.loaded / e.total) * 100);
          return pct;
        }
      },
    }
  );
  return { url: res.data.secure_url, type: resourceType };
};

// ─── Accepted formats ─────────────────────────────────────────────────────────
const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/ogg,video/quicktime";

const FORMAT_LABELS = {
  "image/jpeg": "JPG",
  "image/png": "PNG",
  "image/webp": "WEBP",
  "image/gif": "GIF",
  "video/mp4": "MP4",
  "video/webm": "WEBM",
  "video/ogg": "OGG",
  "video/quicktime": "MOV",
};

const getFileKind = (file) => {
  if (!file) return null;
  if (file.type.startsWith("video/")) return "video";
  if (file.type === "image/gif") return "gif";
  if (file.type === "image/webp") return "webp";
  return "image";
};

// ─── Media Preview (handles image / gif / webp / video) ───────────────────────
const MediaPreview = ({ url, kind, className = "" }) => {
  if (!url) return null;
  if (kind === "video") {
    return (
      <video
        src={url}
        className={`w-full object-cover ${className}`}
        autoPlay
        loop
        muted
        playsInline
      />
    );
  }
  return <img src={url} alt="preview" className={`w-full object-cover ${className}`} />;
};

// ─── File Kind Badge ──────────────────────────────────────────────────────────
const KindBadge = ({ kind }) => {
  const map = {
    video: { label: "VIDEO", color: "bg-blue-100 text-blue-600" },
    gif: { label: "GIF", color: "bg-purple-100 text-purple-600" },
    webp: { label: "WEBP", color: "bg-teal-100 text-teal-600" },
    image: { label: "IMAGE", color: "bg-gray-100 text-gray-500" },
  };
  const { label, color } = map[kind] || map.image;
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide ${color}`}>
      {label}
    </span>
  );
};

// ─── Banner Uploader ──────────────────────────────────────────────────────────
const BannerUploader = ({ label, sublabel, value, mediaKind, onChange }) => {
  const inputRef = useRef();
  const [preview, setPreview] = useState(value || "");
  const [kind, setKind] = useState(mediaKind || null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    setPreview(value || "");
    setKind(mediaKind || null);
  }, [value, mediaKind]);

  const validateFile = (file) => {
    const allowed = ACCEPT.split(",");
    if (!allowed.includes(file.type)) {
      setError(`Unsupported format: ${file.type}. Use JPG, PNG, WEBP, GIF, MP4, WEBM, or MOV.`);
      return false;
    }
    const maxMB = file.type.startsWith("video/") ? 100 : 10;
    if (file.size > maxMB * 1024 * 1024) {
      setError(`File too large. Max size: ${maxMB}MB for ${file.type.startsWith("video/") ? "videos" : "images"}.`);
      return false;
    }
    setError("");
    return true;
  };

  const handleFile = async (file) => {
    if (!file || !validateFile(file)) return;

    const fileKind = getFileKind(file);
    setUploading(true);
    setProgress(5);
    setKind(fileKind);

    // Show local preview immediately
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);

    try {
      // Simulate early progress, then track real upload
      const ticker = setInterval(() => setProgress((p) => Math.min(p + 8, 80)), 300);
      const { url } = await uploadToCloudinary(file);
      clearInterval(ticker);
      setProgress(100);
      setPreview(url);
      onChange(url, fileKind);
      setTimeout(() => setProgress(0), 800);
    } catch (err) {
      console.error(err);
      setError("Upload failed. Please check your Cloudinary credentials.");
      setPreview(value || "");
      setKind(mediaKind || null);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setPreview("");
    setKind(null);
    setProgress(0);
    onChange("", null);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-800">{label}</p>
          <p className="text-xs text-gray-400 mt-0.5">{sublabel}</p>
        </div>
        {kind && <KindBadge kind={kind} />}
      </div>

      {/* Drop Zone */}
      <div
        onClick={() => !uploading && inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`relative rounded-xl border-2 border-dashed overflow-hidden cursor-pointer transition-all duration-200
          ${dragOver ? "border-orange-400 bg-orange-50" : ""}
          ${!dragOver && !preview ? "border-gray-200 bg-gray-50 hover:border-orange-300 hover:bg-orange-50/40" : ""}
          ${!dragOver && preview ? "border-gray-200 hover:border-orange-300" : ""}
          ${uploading ? "cursor-wait" : ""}
        `}
        style={{ minHeight: 158 }}
      >
        {preview ? (
          <>
            <MediaPreview
              url={preview}
              kind={kind}
              className="w-full"
              style={{ minHeight: 158, maxHeight: 200 }}
            />
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-2">
              <span className="bg-white text-gray-900 text-xs font-semibold px-4 py-2 rounded-full shadow-md">
                ↑ Replace
              </span>
              <button
                type="button"
                onClick={handleRemove}
                className="bg-red-500 text-white text-xs font-semibold px-3 py-2 rounded-full shadow-md hover:bg-red-600"
              >
                ✕ Remove
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 py-10 px-4 text-center">
            <div className={`w-11 h-11 rounded-full flex items-center justify-center mb-1 transition-colors ${dragOver ? "bg-orange-100" : "bg-gray-100"}`}>
              <svg className={`w-5 h-5 transition-colors ${dragOver ? "text-orange-500" : "text-gray-400"}`} fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
            </div>
            <p className="text-sm text-gray-500">
              Drop file or{" "}
              <span className="text-orange-500 font-semibold underline underline-offset-2">browse</span>
            </p>
            {/* Format badges */}
            <div className="flex flex-wrap justify-center gap-1 mt-1">
              {["JPG", "PNG", "WEBP", "GIF", "MP4", "WEBM", "MOV"].map((fmt) => (
                <span key={fmt} className="text-[10px] font-semibold bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded">
                  {fmt}
                </span>
              ))}
            </div>
            <p className="text-xs text-gray-400">Images up to 10MB · Videos up to 100MB</p>
          </div>
        )}

        {/* Progress bar */}
        {progress > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
            <div
              className="h-full bg-orange-400 transition-all duration-300 rounded-r"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* Uploading spinner */}
        {uploading && (
          <div className="absolute inset-0 bg-white/70 flex flex-col items-center justify-center gap-2">
            <div className="w-7 h-7 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
            <p className="text-xs text-gray-500 font-medium">{progress}%</p>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <span>⚠</span> {error}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => handleFile(e.target.files[0])}
      />
    </div>
  );
};

// ─── Live Preview ─────────────────────────────────────────────────────────────
const LivePreview = ({ form }) => {
  const [device, setDevice] = useState("desktop");
  const banner = device === "desktop" ? form.desktopBanner : form.mobileBanner;
  const kind = device === "desktop" ? form.desktopKind : form.mobileKind;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Live Preview</p>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {[{ key: "desktop", icon: "🖥" }, { key: "mobile", icon: "📱" }].map((d) => (
            <button
              key={d.key}
              type="button"
              onClick={() => setDevice(d.key)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-150 capitalize ${
                device === d.key ? "bg-white text-orange-500 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {d.icon} {d.key}
            </button>
          ))}
        </div>
      </div>

      <div className={`border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm ${device === "mobile" ? "max-w-[210px] mx-auto w-full" : "w-full"}`}>
        {banner ? (
          <MediaPreview
            url={banner}
            kind={kind}
            className={`w-full object-cover ${device === "mobile" ? "aspect-[9/14]" : "aspect-[16/6]"}`}
          />
        ) : (
          <div className={`w-full bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center text-gray-300 text-xs border-b border-gray-100 ${device === "mobile" ? "aspect-[9/14]" : "aspect-[16/6]"}`}>
            No banner
          </div>
        )}
        <div className="p-4">
          <div className="w-8 h-0.5 bg-orange-400 rounded mb-2" />
          <h2 className="font-bold text-gray-900 text-base leading-snug mb-1">
            {form.heading || <span className="text-gray-300 font-normal italic">Heading…</span>}
          </h2>
          <p className="text-xs text-gray-500 leading-relaxed">
            {form.description || <span className="text-gray-300">Description…</span>}
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminHeroPage = () => {
  const [hero, setHero] = useState(null);
  const [form, setForm] = useState({
    desktopBanner: "",
    desktopKind: null,
    mobileBanner: "",
    mobileKind: null,
    heading: "",
    description: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchHero = async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/hero/get`);

    const data = Array.isArray(res.data.data)
      ? res.data.data[0]
      : res.data.data;

    if (!data) return;

    setHero(data);

    setForm({
      desktopBanner: data.desktopBanner || "",
      desktopKind: data.desktopKind || null,
      mobileBanner: data.mobileBanner || "",
      mobileKind: data.mobileKind || null,
      heading: data.heading || "",
      description: data.description || ""
    });

  } catch (err) {
    console.error(err);
    alert("Failed to load hero data.");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => { fetchHero(); 
  }, []);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/hero/save`, form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      fetchHero();
    } catch (err) {
      console.error(err);
      alert("Update failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-3 text-gray-400">
        <div className="w-8 h-8 border-2 border-orange-100 border-t-orange-400 rounded-full animate-spin" />
        <p className="text-sm">Loading hero data…</p>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Header ── */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-orange-400 rounded-full" />
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">Hero Section</h1>
            <span className="text-xs font-medium bg-orange-50 text-orange-500 border border-orange-200 px-2.5 py-0.5 rounded-full">
              Editor
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block" />
            Changes reflect live
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-7 items-start">

          {/* ── Form ── */}
          <form onSubmit={handleUpdate} className="flex flex-col gap-6">

            {/* Banner Card */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full bg-orange-400" />
                  <p className="text-sm font-semibold text-gray-700">Banner Media</p>
                </div>
                <p className="text-xs text-gray-400">Supports image, GIF, WEBP &amp; video</p>
              </div>
              <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <BannerUploader
                  label="Desktop Banner"
                  sublabel="1440 × 500px recommended"
                  value={form.desktopBanner}
                  mediaKind={form.desktopKind}
                  onChange={(url, kind) => setForm((f) => ({ ...f, desktopBanner: url, desktopKind: kind }))}
                />
                <BannerUploader
                  label="Mobile Banner"
                  sublabel="390 × 600px recommended"
                  value={form.mobileBanner}
                  mediaKind={form.mobileKind}
                  onChange={(url, kind) => setForm((f) => ({ ...f, mobileBanner: url, mobileKind: kind }))}
                />
              </div>
            </div>

            {/* Text Card */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2.5">
                <div className="w-2 h-2 rounded-full bg-orange-400" />
                <p className="text-sm font-semibold text-gray-700">Text Content</p>
              </div>
              <div className="p-6 flex flex-col gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Heading</label>
                  <input
                    type="text"
                    name="heading"
                    value={form.heading}
                    onChange={handleChange}
                    placeholder="Enter your hero heading…"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 bg-gray-50 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all duration-150"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    placeholder="Write a compelling description…"
                    rows={4}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 bg-gray-50 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all duration-150 resize-none leading-relaxed"
                  />
                </div>
              </div>
            </div>

            {/* Save */}
            <button
              type="submit"
              disabled={saving}
              className={`w-full py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-200 shadow-sm
                ${saved ? "bg-green-500 text-white"
                  : saving ? "bg-orange-300 text-white cursor-wait"
                  : "bg-orange-500 hover:bg-orange-600 active:scale-[0.99] text-white"
                }`}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving changes…
                </>
              ) : saved ? <>✓ Changes saved!</> : <>Update Hero Section</>}
            </button>
          </form>

          {/* ── Preview ── */}
          <div className="hidden lg:block bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden sticky top-24">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2.5">
              <div className="w-2 h-2 rounded-full bg-orange-400" />
              <p className="text-sm font-semibold text-gray-700">Preview</p>
            </div>
            <div className="p-6">
              <LivePreview form={form} />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminHeroPage;